import React, { useEffect, useRef, useState } from "react";
import { useHistoryStep } from "../../lib/useHistoryStep";
import { api, formatNok } from "../../lib/api";
import { Check, ChevronRight, Calculator, RotateCcw, ArrowRight, Search } from "lucide-react";
import { inputCls, TextInput } from "./formPrimitives";
import { OTHER_ID, otherLabel, otherHint, filterModels, groupModels } from "./ModelPicker";
import PartsQualityInfo from "./PartsQualityInfo";

const STEPS = ["Velg enhet", "Velg modell", "Velg problem", "Pris"];

export default function PriceCalculator() {
  const [step, setStep] = useState(0);
  useHistoryStep("calcStep", step, setStep);
  const [categories, setCategories] = useState([]);
  const [models, setModels] = useState([]);
  const [problems, setProblems] = useState([]);
  const [category, setCategory] = useState(null);
  const [model, setModel] = useState(null);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [otherText, setOtherText] = useState("");
  const [otherError, setOtherError] = useState("");
  const cardRef = useRef(null);
  const activeStepRef = useRef(null);

  useEffect(() => {
    api.get("/content/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/content/problems").then((r) => setProblems(r.data)).catch(() => {});
  }, []);

  // Keep the active step pill visible inside the horizontally scrollable stepper (mobile).
  useEffect(() => {
    const pill = activeStepRef.current;
    const track = cardRef.current;
    if (!pill || !track) return;
    const left = pill.offsetLeft - (track.clientWidth - pill.offsetWidth) / 2;
    track.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
  }, [step]);

  // After a model selection reveals repair services (step 2) or prices (step 3),
  // close the list and scroll the results container below the sticky header.
  useEffect(() => {
    if (step !== 2 && step !== 3) return;
    const el = cardRef.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: reduce ? "instant" : "smooth", block: "start" });
      });
    });
  }, [step]);

  const chooseCategory = async (c) => {
    setCategory(c);
    setModel(null); setQuery(""); setOtherText(""); setOtherError("");
    const r = await api.get(`/content/models?category_id=${c.id}`);
    setModels(r.data);
    setStep(1);
  };

  const chooseModel = (m) => { setModel(m); setStep(2); };
  const chooseOther = () => { setModel({ id: OTHER_ID, name: otherLabel(category?.name) }); };
  const confirmOther = () => {
    if (!otherText.trim()) { setOtherError("Skriv inn modellen din"); return; }
    setModel({ id: OTHER_ID, name: otherText.trim() });
    setStep(2);
  };

  const toggleProblem = (p) =>
    setSelected((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));

  const calculate = async () => {
    setLoading(true);
    try {
      if (model.id === OTHER_ID) {
        setResult({ lines: selected.map((p) => ({ problem: p, price: null })), total: null, all_known: false });
      } else {
        const r = await api.post("/calculator/price", { model_id: model.id, problems: selected });
        setResult(r.data);
      }
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0); setCategory(null); setModel(null); setSelected([]); setResult(null); setModels([]); setQuery(""); setOtherText(""); setOtherError("");
  };

  const goBook = () => {
    const estimate = result.lines.map((l) => `${l.problem} – ${l.price === null ? "Pris etter diagnostikk" : formatNok(l.price)}`).join(", ");
    window.dispatchEvent(new CustomEvent("mobilhub:prefill-booking", {
      detail: { category: category?.name || "", model, problems: selected, lines: result.lines, total: result.total, estimate },
    }));
    document.querySelector("#bestill")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="priser" className="py-16 sm:py-24 bg-white" data-testid="price-calculator-section">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        <div className="max-w-2xl">
          <span className="text-sm font-semibold uppercase tracking-widest text-electric">Priser</span>
          <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold text-navy">Beregn pris</h2>
          <p className="mt-3 text-ink/70">
            Velg enhet, modell og problem for å se veiledende pris. Endelig pris kan avvike
            etter diagnostikk.
          </p>
        </div>

        {/* Stepper */}
        <div ref={cardRef} data-testid="calc-stepper" className="mt-8 flex items-center gap-2 overflow-x-auto no-scrollbar">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div
                ref={i === step ? activeStepRef : null}
                data-testid={`calc-step-${i}`}
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs sm:text-sm font-medium whitespace-nowrap ${
                  i === step ? "bg-navy text-white" : i < step ? "bg-electric/10 text-electric" : "bg-slate-100 text-ink/50"
                }`}
              >
                <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[11px] ${
                  i < step ? "bg-electric text-white" : i === step ? "bg-warm text-navy" : "bg-slate-200 text-ink/50"
                }`}>
                  {i < step ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                {s}
              </div>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
            </React.Fragment>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-hairline bg-offwhite p-5 sm:p-8 shadow-sm">
          {step === 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="calc-step-category">
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => chooseCategory(c)}
                  className="rounded-xl border border-hairline bg-white px-4 py-5 text-left font-semibold text-navy hover:border-electric hover:shadow-sm transition-all"
                  data-testid={`calc-category-${c.name}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div data-testid="calc-step-model">
              <p className="mb-4 text-sm text-ink/60">Enhet: <span className="font-semibold text-navy">{category?.name}</span></p>
              {models.length > 0 && (
                <div className="relative mb-4">
                  <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Søk etter modell, A-nummer, år eller prosessor ..." className={`${inputCls} pl-10`} data-testid="calc-model-search" />
                </div>
              )}
              {models.length === 0 && <p className="text-ink/60 text-sm mb-3">Ingen modeller lagt inn ennå. Velg «{otherLabel(category?.name)}» under.</p>}
              {groupModels(filterModels(models, query)).map(({ group, items }) => (
                <div key={group || "_"} className="mb-4">
                  {group && <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink/50" data-testid={`calc-model-group-${group}`}>{group}</h4>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {items.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => chooseModel(m)}
                        className="rounded-xl border border-hairline bg-white px-4 py-4 text-left hover:border-electric hover:shadow-sm transition-all"
                        data-testid={`calc-model-${m.name}`}
                      >
                        <span className="block font-semibold text-navy">{m.name}</span>
                        {m.brand && <span className="text-xs text-ink/50">{m.brand}</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {models.length > 0 && filterModels(models, query).length === 0 && (
                <p className="mb-4 text-sm text-ink/60" data-testid="calc-model-empty">Ingen treff på «{query}». Velg «{otherLabel(category?.name)}» under.</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={chooseOther}
                  className={`rounded-xl border bg-white px-4 py-4 text-left hover:border-electric hover:shadow-sm transition-all ${model?.id === OTHER_ID ? "border-electric" : "border-hairline border-dashed"}`}
                  data-testid="calc-model-other"
                >
                  <span className="block font-semibold text-navy">{otherLabel(category?.name)}</span>
                  <span className="text-xs text-ink/50">Pris etter diagnostikk</span>
                </button>
              </div>
              {model?.id === OTHER_ID && (
                <div className="mt-3 flex flex-col sm:flex-row gap-3" data-testid="calc-model-other-form">
                  <div className="flex-1">
                    <TextInput value={otherText} onChange={(e) => { setOtherText(e.target.value); setOtherError(""); }} placeholder={otherHint(category?.name)} error={otherError} data-testid="calc-model-other-input" />
                    {otherError && <span className="mt-1 block text-xs font-medium text-red-600">{otherError}</span>}
                  </div>
                  <button onClick={confirmOther} className="inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navy-800" data-testid="calc-model-other-continue">
                    Fortsett <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
              <button onClick={() => setStep(0)} className="mt-5 text-sm text-electric font-medium">← Tilbake</button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="calc-step-problem">
              <p className="mb-4 text-sm text-ink/60">
                Modell: <span className="font-semibold text-navy">{model?.name}</span>
              </p>
              <div className="flex flex-wrap gap-2.5">
                {problems.map((p) => {
                  const on = selected.includes(p);
                  return (
                    <button
                      key={p}
                      onClick={() => toggleProblem(p)}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
                        on ? "border-electric bg-electric text-white" : "border-hairline bg-white text-navy hover:border-electric"
                      }`}
                      data-testid={`calc-problem-${p}`}
                    >
                      {on && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />}{p}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center justify-between">
                <button onClick={() => setStep(1)} className="text-sm text-electric font-medium">← Tilbake</button>
                <button
                  onClick={calculate}
                  disabled={selected.length === 0 || loading}
                  className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white disabled:opacity-40 hover:bg-navy-800 transition-colors"
                  data-testid="calc-calculate-button"
                >
                  <Calculator className="w-4 h-4" /> {loading ? "Beregner ..." : "Vis pris"}
                </button>
              </div>
            </div>
          )}

          {step === 3 && result && (
            <div data-testid="calc-step-result">
              <p className="text-sm text-ink/60">
                {category?.name} · <span className="font-semibold text-navy">{model?.name}</span>
              </p>
              <div className="mt-4 divide-y divide-hairline rounded-xl border border-hairline bg-white">
                {result.lines.map((l) => (
                  <div key={l.problem} className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`calc-result-${l.problem}`}>
                    <span className="text-sm font-medium text-navy">{l.problem}</span>
                    <span className={`shrink-0 text-right text-sm font-semibold ${l.price === null ? "text-ink/50" : "text-electric"}`}>
                      {l.price === null ? "Pris etter diagnostikk" : formatNok(l.price)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between rounded-xl bg-navy px-4 py-4 text-white">
                <span className="font-heading text-lg font-bold">Estimert totalt</span>
                <span className="font-heading text-xl font-bold text-warm">
                  {result.total === null ? "Pris etter diagnostikk" : formatNok(result.total)}
                </span>
              </div>
              <p className="mt-3 text-xs text-ink/50">
                Veiledende priser. Endelig pris bekreftes etter diagnostikk av enheten.
              </p>
              {category?.name?.toLowerCase().includes("iphone") && (
                <div className="mt-4"><PartsQualityInfo testid="calc-parts-quality" /></div>
              )}
              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={goBook}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-warm px-6 py-3 text-sm font-semibold text-navy hover:bg-warm-dark transition-colors"
                  data-testid="calc-book-button"
                >
                  Bestill reparasjon <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={reset}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-hairline bg-white px-6 py-3 text-sm font-semibold text-navy hover:border-electric transition-colors"
                  data-testid="calc-reset-button"
                >
                  <RotateCcw className="w-4 h-4" /> Start på nytt
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
